import { Paragraph } from '@fs/zion-ui'
import React from 'react'
import { Trans } from 'react-i18next'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { type GroupMember } from '@fs/zion-tree-types'
import { useFeatureFlag } from '@fs/zion-flags'
import { useMembersContext } from '../../../providers/MembersProvider'
import { type GedcomData } from '../../../services/useGedcomDataHook'
import { usePrivatePersonCountContext } from '../../../providers/PrivatePersonCountProvider'

const NoSpacerParagraph = (props: Omit<React.ComponentProps<typeof Paragraph>, 'size'>): JSX.Element => (
  <div>
    <Paragraph size="sm" {...props} />
  </div>
)

type Props = {
  gedcomData?: GedcomData
}

export default function AboutThisTreeContent({ gedcomData }: Props): JSX.Element {
  const { treeDetails } = useGroupsContext()
  const { privatePersonCount } = usePrivatePersonCountContext()
  const newSettingsPageOn = useFeatureFlag('treeWeb_groupManagement_newSettingsPage').isOn
  const { admins } = useMembersContext()
  const isOwner = (admin: GroupMember): boolean => admin.memberCisId === treeDetails?.ownerCisId
  const owner = admins.find(isOwner)
  const coOwners = admins.filter((admin) => !isOwner(admin))

  return (
    <>
      {!!privatePersonCount && (
        <NoSpacerParagraph>
          <Trans i18nKey="group-management.tree-person-count.label" values={{ count: privatePersonCount }} />
        </NoSpacerParagraph>
      )}

      {gedcomData && (
        <>
          {!newSettingsPageOn && (
            <NoSpacerParagraph>
              <Trans i18nKey="group-management.upload-errors.label" values={{ count: gedcomData.errorCount }} />
            </NoSpacerParagraph>
          )}
          <NoSpacerParagraph>
            <Trans i18nKey="group-management.upload-date.label" values={{ date: gedcomData.uploadDate }} />
          </NoSpacerParagraph>
          <NoSpacerParagraph>
            <Trans i18nKey="group-management.file-name.label" values={{ fileName: gedcomData.fileName }} />
          </NoSpacerParagraph>
        </>
      )}
      {!newSettingsPageOn && owner?.userProfileName && (
        <NoSpacerParagraph>
          <Trans i18nKey="group-management.owner-colon.label" values={{ name: owner.userProfileName }} />
        </NoSpacerParagraph>
      )}
      {!newSettingsPageOn &&
        coOwners.map((coOwner) => (
          <NoSpacerParagraph key={coOwner.memberCisId}>
            <Trans i18nKey="group-management.co-owner-colon.label" values={{ name: coOwner.userProfileName }} />
          </NoSpacerParagraph>
        ))}
    </>
  )
}
